import { record } from '@rrweb/record';
import { EventBatcher, type BatchSink } from './batcher';
import { buildRecordOptions, type RedactionConfig, type RrEvent } from './redaction';

/**
 * Startet und stoppt rrweb. Wird ausschließlich nach Consent bzw. nach `session.started`
 * benutzt (Spec Kap. 9: keine Bildschirmdaten vor Freigabe, auch nicht zum Puffern).
 */
export class Recorder {
  private stopFn: (() => void) | null = null;
  private readonly batcher: EventBatcher;

  constructor(
    private readonly cfg: RedactionConfig,
    sink: BatchSink,
  ) {
    this.batcher = new EventBatcher(sink);
  }

  get running(): boolean {
    return this.stopFn !== null;
  }

  /** Startet rrweb neu (erzeugt dabei immer einen frischen FullSnapshot). */
  start(): void {
    this.stop();
    const stop = record<RrEvent>(buildRecordOptions(this.cfg, (e) => this.batcher.add(e)));
    // rrweb liefert undefined, wenn es nicht starten kann (z. B. kein DOM).
    this.stopFn = stop ?? (() => undefined);
  }

  takeFullSnapshot(): void {
    if (!this.running) return;
    record.takeFullSnapshot(true);
  }

  /**
   * Löst eine rrweb-nodeId (wie sie die Konsole aus dem Replay kennt) in den DOM-Knoten auf.
   * Nur während der Aufzeichnung: der Mirror gehört zur laufenden Aufzeichnung.
   */
  getNode(id: number): Node | null {
    if (!this.running) return null;
    try {
      return record.mirror.getNode(id) ?? null;
    } catch {
      return null;
    }
  }

  stop(): void {
    if (!this.stopFn) return;
    const stop = this.stopFn;
    this.stopFn = null;
    try {
      stop();
    } catch {
      // rrweb-Interna sollen das Beenden nie blockieren.
    }
    this.batcher.clear();
  }
}
